import { Routes } from '@angular/router';
import { redirectUnauthorizedTo } from './guards/auth.guard';
import { requirePermission, requireOrg } from './guards/permission.guard';

export const routes: Routes = [
  {
    // Neutral interstitial during global sign-out (no guard: shown while the
    // session is being torn down). See SigningOutComponent.
    path: 'signing-out',
    loadComponent: () =>
      import('./pages/signing-out/signing-out.component').then((m) => m.SigningOutComponent),
  },
  {
    path: '__/auth',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent,
      ),
  },
  {
    path: 'auth-redirect',
    loadComponent: () =>
      import('./pages/auth-redirect/auth-redirect.component').then(
        (m) => m.AuthRedirectComponent,
      ),
  },
  {
    // Public, read-only view of a published guide (no account needed) —
    // content comes from the `helpDocs` function via the /docs-api rewrite.
    path: 'g/:publicId',
    loadComponent: () =>
      import('./pages/guide-public/guide-public.component').then((m) => m.GuidePublicComponent),
  },
  {
    // Data residency: the active org's region can't be reached (requireOrg).
    path: 'region-unavailable',
    loadComponent: () =>
      import('./pages/region-unavailable/region-unavailable.component').then(
        (m) => m.RegionUnavailableComponent,
      ),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    // Login is centralized in the auth portal. This route only needs to resolve
    // so code that navigates to `/login` (e.g. sign-out) hands off to the portal:
    // the guard calls bridge.login() for unauthenticated users before the (empty)
    // component renders.
    path: 'login',
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
  },
  {
    path: '',
    loadComponent: () => import('./layout/layout.component').then((m) => m.LayoutComponent),
    canActivate: [redirectUnauthorizedTo('/login')],
    children: [
      {
        path: 'new',
        loadComponent: () => import('./pages/new-user/new-user.component').then((m) => m.NewUserComponent),
      },
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'team',
        loadComponent: () => import('./pages/team/team.component').then((m) => m.TeamComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'team/:id',
        loadComponent: () =>
          import('./pages/agent-detail/agent-detail.component').then((m) => m.AgentDetailComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'chatbots',
        loadComponent: () =>
          import('./pages/chatbots/chatbots.component').then((m) => m.ChatbotsComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'chatbots/:id',
        loadComponent: () =>
          import('./pages/chatbot-detail/chatbot-detail.component').then(
            (m) => m.ChatbotDetailComponent,
          ),
        canActivate: [requireOrg()],
      },
      {
        path: 'conversations',
        loadComponent: () =>
          import('./pages/conversations/conversations.component').then(
            (m) => m.ConversationsComponent,
          ),
        canActivate: [requireOrg()],
      },
      {
        path: 'conversations/:id',
        loadComponent: () =>
          import('./pages/conversation-detail/conversation-detail.component').then(
            (m) => m.ConversationDetailComponent,
          ),
        canActivate: [requireOrg()],
      },
      {
        path: 'knowledge',
        loadComponent: () =>
          import('./pages/knowledge/knowledge.component').then((m) => m.KnowledgeComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./pages/reports/reports.component').then((m) => m.ReportsComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'evals',
        loadComponent: () => import('./pages/evals/evals.component').then((m) => m.EvalsComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'guides',
        loadComponent: () =>
          import('./pages/guides/guides.component').then((m) => m.GuidesComponent),
        canActivate: [requireOrg()],
      },
      {
        path: 'guides/:id',
        loadComponent: () =>
          import('./pages/guide-detail/guide-detail.component').then(
            (m) => m.GuideDetailComponent,
          ),
        canActivate: [requireOrg()],
      },
      {
        path: 'organizations',
        loadComponent: () =>
          import('./pages/organizations/organizations.component').then((m) => m.OrganizationsComponent),
        canActivate: [requireOrg(), requirePermission('members.read')],
      },
      {
        path: 'account',
        loadComponent: () => import('./pages/account/account.component').then((m) => m.AccountComponent),
      },
      {
        path: 'settings',
        redirectTo: 'account',
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
    ],
  },
];
